import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Baby,
  ClipboardList,
  Megaphone,
  Trophy,
  Flag,
  Apple,
  Tent,
  Medal,
  Utensils,
  Moon,
  Flame,
  Clock,
  Backpack,
  CalendarDays,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { CampProgram } from '../data/programData';
import type { AgeGroup } from '../types';
import type { Activity } from '../types';

const ACTIVITY_ICONS: Record<string, LucideIcon> = {
  registration: ClipboardList,
  groups: Users,
  info: Megaphone,
  game: Trophy,
  flag: Flag,
  snacks: Apple,
  tent: Tent,
  stadium: Medal,
  dinner: Utensils,
  evening: Moon,
};

const initialCamp = (): AgeGroup => {
  try {
    const stored = sessionStorage.getItem('sw_ageGroup');
    if (stored === 'kids' || stored === 'teens') return stored;
  } catch {
    // sessionStorage unavailable — fall through to default
  }
  return 'teens';
};

// Which year's program to show is the caller's decision: the required
// `programs` prop stops anyone from accidentally rendering an unfinished
// season's data. `closingNote` is the colored note under the timeline.
interface ProgramProps {
  programs: CampProgram[];
  closingNote?: React.ReactNode;
}

const Program: React.FC<ProgramProps> = ({ programs, closingNote }) => {
  const [camp, setCamp] = useState<AgeGroup>(initialCamp);
  const [activeDay, setActiveDay] = useState(0);

  const program = programs.find((c) => c.id === camp) ?? programs[0];
  const day = program.days[Math.min(activeDay, program.days.length - 1)];
  const isTeens = program.id === 'teens';

  const selectCamp = (next: AgeGroup) => {
    setCamp(next);
    setActiveDay(0);
  };

  const TimelineActivity: React.FC<{ activity: Activity; isLast: boolean }> = ({ activity, isLast }) => {
    const Icon = ACTIVITY_ICONS[activity.icon] ?? CalendarDays;
    return (
    <div className="flex items-start space-x-4 group">
      {/* Timeline line and time bubble */}
      <div className="flex flex-col items-center">
        <div
          className={`text-white px-3 py-2 rounded-full font-bold text-sm shadow-lg ${
            isTeens
              ? 'bg-gradient-to-br from-accent-500 to-blue-600'
              : 'bg-gradient-to-br from-primary-500 to-secondary-500'
          }`}
        >
          {activity.time}
        </div>
        {!isLast && (
          <div
            className={`w-0.5 h-16 mt-2 ${
              isTeens
                ? 'bg-gradient-to-b from-accent-300 to-blue-300'
                : 'bg-gradient-to-b from-primary-300 to-secondary-300'
            }`}
          ></div>
        )}
      </div>

      {/* Activity content */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className={`flex-1 bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 group-hover:scale-[1.02] border border-gray-200 ${
          isLast ? 'mb-0' : 'mb-8'
        }`}
      >
        <div className="flex items-start space-x-4">
          <div
            className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isTeens ? 'bg-accent-50 text-accent-600' : 'bg-primary-50 text-primary-600'
            }`}
          >
            <Icon className="w-6 h-6" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-xl text-gray-800 mb-2">{activity.title}</h4>
            <p className="text-gray-600 leading-relaxed">{activity.description}</p>
          </div>
        </div>
      </motion.div>
    </div>
    );
  };

  return (
    // No section id here: the component is embedded (archive accordion);
    // the home page's #program anchor lives on the wrapper in App.tsx.
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
            Nometnes Programma
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Divas nometnes vienā nedēļā — izvēlies savu un apskati, kas katru dienu notiks!
          </p>
        </motion.div>

        {/* Camp toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-wrap justify-center gap-4 mb-12"
        >
          {programs.map((p) => {
            const teens = p.id === 'teens';
            const active = program.id === p.id;
            const Icon = teens ? Users : Baby;
            return (
              <motion.button
                key={p.id}
                onClick={() => selectCamp(p.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center space-x-3 px-6 py-3 rounded-full font-bold text-sm sm:text-base transition-all duration-300 ${
                  active
                    ? teens
                      ? 'bg-gradient-to-r from-accent-500 to-blue-600 text-white shadow-lg'
                      : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                    : teens
                      ? 'bg-accent-50 border border-accent-200 text-accent-700 hover:bg-accent-100'
                      : 'bg-primary-50 border border-primary-200 text-primary-700 hover:bg-primary-100'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span>{p.label}</span>
                <span
                  className={`text-xs font-medium ${
                    active ? 'text-white/80' : teens ? 'text-accent-500' : 'text-primary-500'
                  }`}
                >
                  {p.dates}
                </span>
              </motion.button>
            );
          })}
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Day tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-wrap justify-center mb-12 bg-white rounded-2xl shadow-lg p-2"
          >
            {program.days.map((d, index) => (
              <motion.button
                key={`${camp}-${index}`}
                onClick={() => setActiveDay(index)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-8 py-4 m-1 rounded-xl font-bold text-sm sm:text-base transition-all duration-300 ${
                  activeDay === index
                    ? isTeens
                      ? 'bg-gradient-to-r from-accent-500 to-blue-600 text-white shadow-lg'
                      : 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-lg'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <div>{d.day}</div>
                <div className="text-xs opacity-80">{d.date}</div>
              </motion.button>
            ))}
          </motion.div>

          {/* Timeline */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${camp}-${activeDay}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="text-center mb-8">
                <h3 className="text-3xl font-bold text-gray-800 mb-2">{day.day}</h3>
                <p className="text-xl text-gray-600">{day.date}</p>
              </div>
              <div>
                {day.activities.map((activity, index) => (
                  <TimelineActivity
                    key={`${activity.time}-${activity.title}`}
                    activity={activity}
                    isLast={index === day.activities.length - 1}
                  />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Closing weekend note */}
          {closingNote && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className={`mt-10 rounded-2xl border p-6 text-center ${
                isTeens ? 'bg-accent-50 border-accent-200' : 'bg-primary-50 border-primary-200'
              }`}
            >
              <div className="flex items-start justify-center gap-3 text-left sm:items-center">
                <Flame className={`w-6 h-6 flex-shrink-0 ${isTeens ? 'text-accent-600' : 'text-primary-600'}`} />
                <p className="text-gray-700 text-lg leading-relaxed">{closingNote}</p>
              </div>
            </motion.div>
          )}
        </div>

        {/* Additional info */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-16 bg-white rounded-3xl shadow-xl p-8 max-w-4xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Svarīga informācija
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-bold text-primary-600 mb-3 flex items-center">
                <Clock className="w-4 h-4 mr-2" />
                Laika grafiks
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Pirmajā dienā reģistrācija no 10:30</li>
                <li>• Otrajā dienā sākums 11:00</li>
                <li>• Vakariņas 17:30</li>
                <li>• Diena noslēdzas ap 20:00</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-accent-600 mb-3 flex items-center">
                <Backpack className="w-4 h-4 mr-2" />
                Ko ņemt līdzi
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Sporta apģērbu un ērtus apavus</li>
                <li>• Cepuri un sauļošanās krēmu</li>
                <li>• Ūdens pudeli</li>
                <li>• Uzkodas un vakariņas nodrošinām mēs</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-primary-600 mb-3 flex items-center">
                <Baby className="w-4 h-4 mr-2" />
                Vecākiem
              </h4>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Bērniem līdz 7 gadu vecumam nepieciešama vecāku pavadība</li>
                <li>• Bērnu pavada vecāks vai cits pieaugušais, kurš ir atbildīgs par bērnu visas dienas garumā</li>
                <li>• Dalība ir pilnīgi bez maksas</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              Visi pasākumi notiek Stadionā Viļānos · Jautājumu gadījumā zvaniet +371 24 904 140
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Program;
